import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import moment from "moment";
import "moment/locale/en-gb";
import "moment/locale/ru";
import englishTranslations from "./manual-en-overrides.json";

export const DEFAULT_LOCALE = "ru";
export const SUPPORTED_LOCALES = ["ru", "en"];
const STORAGE_KEY = "speakid_locale";

const I18nContext = createContext(null);

const interpolate = (value, params = {}) =>
  Object.entries(params).reduce(
    (result, [key, replacement]) =>
      result.replaceAll(`{{${key}}}`, String(replacement)),
    value,
  );

const getInitialLocale = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
};

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const setLocale = useCallback((nextLocale) => {
    if (!SUPPORTED_LOCALES.includes(nextLocale)) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, nextLocale);
    } catch {
      // The UI still switches if storage is unavailable.
    }
    setLocaleState(nextLocale);
  }, []);

  const t = useCallback(
    (source, params) => {
      const translated =
        locale === "en" ? englishTranslations[source] || source : source;
      return interpolate(translated, params);
    },
    [locale],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title =
      locale === "en"
        ? "SPEAKID — Platform for English Teachers"
        : "SPEAKID — Платформа для преподавателей английского";
    document
      .querySelector('meta[property="og:locale"]')
      ?.setAttribute("content", locale === "en" ? "en_US" : "ru_RU");
    moment.locale(locale === "en" ? "en-gb" : "ru");
  }, [locale]);

  useEffect(() => {
    const syncLocale = (event) => {
      if (
        event.key === STORAGE_KEY &&
        SUPPORTED_LOCALES.includes(event.newValue)
      ) {
        setLocaleState(event.newValue);
      }
    };
    window.addEventListener("storage", syncLocale);
    return () => window.removeEventListener("storage", syncLocale);
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return context;
};
